import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  Snackbar,
  Tooltip,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Edit,
  Block,
  Search,
  People,
  Storefront,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { TableShimmer } from '../../components/common/Shimmer';
import vendorService from '../../services/vendorService';
import { Vendor, CreateVendorRequest, User } from '../../types';
import { useAuth } from '../../hooks/useAuth';

const vendorSchema = yup.object({
  name: yup.string().required('Vendor name is required').min(2, 'Name must be at least 2 characters'),
  code: yup.string().optional(),
  contact_person: yup.string().optional(),
  phone: yup.string().optional(),
  email: yup.string().email('Enter a valid email address').optional(),
  address: yup.string().optional(),
  description: yup.string().optional(),
});

const VendorsPage: React.FC = () => {
  const { user } = useAuth();
  const canManage = user?.role === 'super_admin' || user?.role === 'cluster_level_manager';

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Drill-down: employees hired through one vendor
  const [usersDialog, setUsersDialog] = useState<{
    open: boolean;
    vendor: Vendor | null;
    users: User[];
    loading: boolean;
  }>({ open: false, vendor: null, users: [], loading: false });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'success' | 'error' | 'warning' | 'info',
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateVendorRequest>({
    resolver: yupResolver(vendorSchema) as any,
  });

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info' = 'error'
  ) => setSnackbar({ open: true, message, severity });

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await vendorService.getVendors({
        search: search || undefined,
        ...(showInactive ? {} : { is_active: true }),
        limit: 200,
      });
      setVendors(response.vendors);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  }, [search, showInactive]);

  useEffect(() => {
    const t = setTimeout(fetchVendors, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchVendors, search]);

  const openCreate = () => {
    setEditing(null);
    reset({
      name: '',
      code: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      description: '',
    });
    setOpenDialog(true);
  };

  const openEdit = (vendor: Vendor) => {
    setEditing(vendor);
    reset({
      name: vendor.name,
      code: vendor.code || '',
      contact_person: vendor.contact_person || '',
      phone: vendor.phone || '',
      email: vendor.email || '',
      address: vendor.address || '',
      description: vendor.description || '',
    });
    setOpenDialog(true);
  };

  const onSubmit = async (data: CreateVendorRequest) => {
    try {
      setSubmitting(true);
      // Strip empty optional strings so the backend stores null rather than ''
      const payload: CreateVendorRequest = { name: data.name };
      (['code', 'contact_person', 'phone', 'email', 'address', 'description'] as const).forEach(
        (k) => {
          const v = data[k];
          if (v && String(v).trim()) (payload as any)[k] = String(v).trim();
        }
      );

      if (editing) {
        await vendorService.updateVendor(editing.id, payload);
        showSnackbar('Vendor updated successfully', 'success');
      } else {
        await vendorService.createVendor(payload);
        showSnackbar('Vendor created successfully', 'success');
      }
      setOpenDialog(false);
      reset();
      fetchVendors();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save vendor';
      showSnackbar(
        msg.includes('already exists') ? 'A vendor with that name or code already exists.' : msg,
        msg.includes('already exists') ? 'warning' : 'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (vendor: Vendor) => {
    const count = vendor.total_user_count ?? 0;
    const warning =
      count > 0
        ? `\n\n${count} employee record(s) are linked to this vendor. They keep their vendor history and are NOT deleted or deactivated.`
        : '';
    if (!window.confirm(`Deactivate "${vendor.name}"?${warning}`)) return;

    try {
      await vendorService.deactivateVendor(vendor.id);
      showSnackbar(`"${vendor.name}" deactivated`, 'success');
      fetchVendors();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to deactivate vendor', 'error');
    }
  };

  const openVendorUsers = async (vendor: Vendor) => {
    setUsersDialog({ open: true, vendor, users: [], loading: true });
    try {
      const res = await vendorService.getVendorUsers(vendor.id, { limit: 200 });
      setUsersDialog({ open: true, vendor, users: res.users, loading: false });
    } catch (err: any) {
      setUsersDialog({ open: false, vendor: null, users: [], loading: false });
      showSnackbar(err.response?.data?.message || 'Failed to load employees', 'error');
    }
  };

  const totalHired = vendors.reduce((sum, v) => sum + (v.user_count || 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Vendors
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Recruitment agencies staff are hired through
            {vendors.length > 0 && ` — ${totalHired} active employee(s) across ${vendors.length} vendor(s)`}
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Add Vendor
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by name, code or contact"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              size="small"
            />
          }
          label="Show deactivated"
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Vendor</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell align="center">Employees Hired</TableCell>
              <TableCell align="center">Status</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableShimmer rows={5} columns={canManage ? 6 : 5} />
            ) : vendors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 6 : 5} align="center" sx={{ py: 6 }}>
                  <Storefront sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    {search ? 'No vendors match your search' : 'No vendors yet'}
                  </Typography>
                  {canManage && !search && (
                    <Button sx={{ mt: 1 }} onClick={openCreate} startIcon={<Add />}>
                      Add your first vendor
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              vendors.map((vendor) => (
                <TableRow key={vendor.id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 500 }}>{vendor.name}</Typography>
                    {vendor.description && (
                      <Typography variant="caption" color="text.secondary">
                        {vendor.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {vendor.code ? <Chip label={vendor.code} size="small" /> : '—'}
                  </TableCell>
                  <TableCell>
                    {vendor.contact_person || vendor.phone || vendor.email ? (
                      <Box>
                        {vendor.contact_person && (
                          <Typography variant="body2">{vendor.contact_person}</Typography>
                        )}
                        {vendor.phone && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {vendor.phone}
                          </Typography>
                        )}
                        {vendor.email && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {vendor.email}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View employees hired through this vendor">
                      <Chip
                        icon={<People sx={{ fontSize: 16 }} />}
                        label={vendor.user_count ?? 0}
                        size="small"
                        color={(vendor.user_count ?? 0) > 0 ? 'primary' : 'default'}
                        variant={(vendor.user_count ?? 0) > 0 ? 'filled' : 'outlined'}
                        onClick={() => openVendorUsers(vendor)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                    {(vendor.total_user_count ?? 0) > (vendor.user_count ?? 0) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {(vendor.total_user_count ?? 0) - (vendor.user_count ?? 0)} inactive
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={vendor.is_active ? 'Active' : 'Deactivated'}
                      size="small"
                      color={vendor.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Tooltip title="Edit vendor">
                        <IconButton size="small" onClick={() => openEdit(vendor)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          vendor.is_active
                            ? 'Deactivate (employees keep their vendor history)'
                            : 'Already deactivated'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="warning"
                            disabled={!vendor.is_active}
                            onClick={() => handleDeactivate(vendor)}
                          >
                            <Block fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / edit */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add Vendor'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={8}>
                <TextField
                  {...register('name')}
                  label="Vendor Name"
                  fullWidth
                  required
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  {...register('code')}
                  label="Code"
                  fullWidth
                  error={!!errors.code}
                  helperText={errors.code?.message || 'Optional'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField {...register('contact_person')} label="Contact Person" fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField {...register('phone')} label="Phone" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register('email')}
                  label="Email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField {...register('address')} label="Address" fullWidth multiline rows={2} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register('description')}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Saving...' : editing ? 'Save Changes' : 'Create Vendor'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Employees hired through a vendor */}
      <Dialog
        open={usersDialog.open}
        onClose={() => setUsersDialog((p) => ({ ...p, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Employees hired via {usersDialog.vendor?.name}
          {usersDialog.users.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {usersDialog.users.length} record(s)
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {usersDialog.loading ? (
            <Table>
              <TableBody>
                <TableShimmer rows={5} columns={5} />
              </TableBody>
            </Table>
          ) : usersDialog.users.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No employees are linked to this vendor yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Store</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersDialog.users.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        {u.first_name} {u.last_name}
                      </TableCell>
                      <TableCell>{u.employee_id}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>
                        {u.role?.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell>{u.store?.name || '—'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={u.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={u.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsersDialog((p) => ({ ...p, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorsPage;
